// ** Amplify
import { API } from "aws-amplify"
import {
  adminsApiGet,
  adminsApiPut,
  adminsApiPost,
  adminsApiDel
} from "@helpers/adminsApiClient"
import { handleApiAuthError } from "@helpers/authSession"
// **  store names
import { STORES } from "@constants/redux"

// ** main app Logger
import { appLogger } from "@helpers/logger"

// ** constants
import { globalConst } from "@constants/global"

// **  utility
import { isEmpty, drop, omit } from "lodash"

// ** fuzzy search support
import Fuse from "fuse.js"

const shouldSkipProtectedDashboardCalls = () => {
  // Admin dashboard must be server-authoritative across browsers.
  return false
}

const V2_BACKEND_BASE = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_V2_BACKEND_URL ||
  ""
).replace(/\/+$/, "")
const hasV2Backend = () => V2_BACKEND_BASE.length > 0

const getV2Headers = () => {
  if (typeof window === "undefined") return { "Content-Type": "application/json" }
  const token = localStorage.getItem("accessToken")
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" }
}

const v2Request = async (path, options = {}) => {
  const response = await fetch(`${V2_BACKEND_BASE}${path}`, {
    method: options.method || "GET",
    headers: getV2Headers(),
    body: options.body ? JSON.stringify(options.body) : undefined
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `v2 request failed (${response.status})`)
  }
  if (response.status === 204) return null
  return await response.json()
}

const putJsonWithTimeout = async (url, body = {}, timeoutMs = 2000) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: getV2Headers(),
      body: JSON.stringify(body || {}),
      signal: controller.signal
    })
    if (!response.ok) throw new Error(`put failed (${response.status})`)
    if (response.status === 204) return null
    return await response.json().catch(() => ({}))
  } finally {
    clearTimeout(timer)
  }
}

const normalizeShowRoomItem = (item = {}) => {
  const normalized = { ...item }
  const attrs =
    normalized.attributes && typeof normalized.attributes === "object" && !Array.isArray(normalized.attributes)
      ? normalized.attributes
      : {}
  const media = Array.isArray(normalized.media) ? normalized.media : []
  const images = Array.isArray(normalized.images) ? normalized.images : media
  const pk = normalized.pk || normalized.id || normalized.itemId || normalized._id
  const title = normalized.title || normalized.descr || attrs.title || normalized.name || "Untitled"
  const titleAr = normalized.titleAr || normalized.title_ar || attrs.titleAr || attrs.title_ar || normalized.name_ar || ""
  const description = normalized.description || normalized.descr || attrs.description || ""
  const createdAt = normalized.createdAt || normalized.created_at || new Date().toISOString()
  const updatedAt = normalized.updatedAt || normalized.updated_at || createdAt
  const explicitStatus = normalized.status || normalized.approval_status
  const status =
    explicitStatus ||
    (normalized.published === true ? "PUBLISHED" : "PENDING")

  return {
    ...normalized,
    pk,
    id: normalized.id || pk,
    ref: normalized.ref || String(pk || "").slice(0, 8),
    title,
    titleAr,
    description,
    descr: normalized.descr || title,
    descrAr: normalized.descrAr || attrs.descrAr || attrs.description_ar || "",
    images,
    media,
    cover: normalized.cover || attrs.cover || (images.length ? images[0] : null),
    coverUrl: normalized.coverUrl || attrs.coverUrl || "",
    createdAt,
    updatedAt,
    status,
    approval_status: normalized.approval_status || (status === "PENDING" ? "PENDING" : status),
    change_status: normalized.change_status || "NO_CHANGES",
    published: normalized.published === true || status === "PUBLISHED"
  }
}

const isPendingShowRoomItem = (item = {}) => {
  const status = String(item.status || item.approval_status || "").toUpperCase()
  if (status === "PENDING") return true
  if (item.published === true) return false
  return status !== "PUBLISHED" && status !== "APPROVED" && status !== "ACTIVE"
}

const filterShowRoomItemsByAck = (items = [], ack = "all") => {
  if (ack === "pending") {
    return items.filter((item) => isPendingShowRoomItem(item))
  }
  if (ack === "all" || ack === "items") {
    return items.filter((item) => !isPendingShowRoomItem(item))
  }
  if (ack === "new") {
    // "New items" should never include pending records.
    return items.filter((item) => !isPendingShowRoomItem(item))
  }
  return items
}

const getAdminsApiDirect = async (path, options = {}) => {
  const method = (options.method || "GET").toUpperCase()
  if (hasV2Backend()) {
    if (method === "GET") return adminsApiGet(path)
    if (method === "PUT") return adminsApiPut(path, options.body || {})
    if (method === "POST") return adminsApiPost(path, options.body || {})
    if (method === "DELETE") return adminsApiDel(path)
  }
  if (method === "GET") {
    return await API.get("adminsApi", path, {})
  }
  if (method === "PUT") {
    return await API.put("adminsApi", path, { body: options.body || {} })
  }
  if (method === "POST") {
    return await API.post("adminsApi", path, { body: options.body || {} })
  }
  if (method === "DELETE") {
    return await API.del("adminsApi", path, {})
  }
  throw new Error(`Unsupported method: ${method}`)
}

const LOCAL_SHOWROOMS_KEY = "local_showrooms_companies"

const readLocalShowRooms = () => {
  if (typeof window === "undefined") return []
  try {
    const raw = localStorage.getItem(LOCAL_SHOWROOMS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch (_error) {
    return []
  }
}

const writeLocalShowRooms = (items) => {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(LOCAL_SHOWROOMS_KEY, JSON.stringify(items))
  } catch (_error) {
    // ignore storage quota/transient errors
  }
}

const findLocalShowRoom = (pk) => {
  const list = readLocalShowRooms()
  return list.find((item) => item.pk === pk) || null
}

const upsertLocalShowRoom = (pk, patch) => {
  const list = readLocalShowRooms()
  const index = list.findIndex((item) => item.pk === pk)
  if (index === -1) return list
  const next = [...list]
  next[index] = { ...next[index], ...patch, pk }
  writeLocalShowRooms(next)
  return next
}

const removeLocalShowRoom = (pk) => {
  const list = readLocalShowRooms()
  const next = list.filter((item) => item.pk !== pk)
  writeLocalShowRooms(next)
  return next
}

const isLocalShowRoomPk = (pk) =>
  typeof pk === "string" && pk.startsWith("local-showroom-")

/** initla state for auth store  */
const initialState = {
  data: [],
  sales: [],
  items: [],
  count_new:0,
  count_pending:0,
  count: "",
  current: {},
  currentItem:{},
  perPageValues: [100, 150, 200, 250, 300],
  itemPerPage: 100,
  subitemPerPage: 100,
  currentPage: 1,
  currentPageItems: 1,
  filters: {
    status: {
      value: "ALL",
      label: "label_all"
    }
  },
  filtersItems: {
    parent: {
      value: "ALL",
      label: "label_all"
    },
    status: {
      value: "ALL",
      label: "label_all"
    }
  },
  message: "",
  keyword: null,
  keyWordItems: null,
  keywordItems: null,
  action: globalConst.action.NONE
}

export const showRooms = {
  state: initialState,

  reducers: {
    /**
     * Set translate message
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setMessage: (s, payload) => {
      return {
        ...s,
        message: payload
      }
    },
    setCount_new: (s, payload) => {
      return {
        ...s,
        count_new: payload
      }
    },
    setCurrentPage: (s, payload) => {
      return {
        ...s,
        currentPage: payload
      }
    },
    setCount_pending: (s, payload) => {
      return {
        ...s,
        count_pending: payload
      }
    },
    setCurrentItem: (s, payload) => {
      return {
        ...s,
        currentItem: payload
      }
    },

    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCurrentPageItems: (s, payload) => {
      return {
        ...s,
        currentPageItems: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCount: (s, payload) => {
      return {
        ...s,
        count: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setData: (s, payload) => {
      return {
        ...s,
        data: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateItem: (s, payload) => {
      const index = s.items.findIndex((e) => e.pk === payload.pk)
      const item = s.items[index]
      item.images = item.images.filter((e) => e !== payload.image)
      const _data = s.items.filter((e) => e.pk !== payload.pk)
      _data[index] = item

      return {
        ...s,
        items: _data
      }
    },

    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    updateSingleItem: (s, payload) => {
      const index = s.items.findIndex((e) => e.pk === payload.pk)
      if (index === -1) {
        // Item not found, return original state
        return s
      }
      
      const _data = [...s.items] // Create a copy of the array
      _data[index] = { ..._data[index], ...payload } // Merge the updates

      return {
        ...s,
        items: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setItems: (s, payload) => {
      return {
        ...s,
        items: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setSales: (s, payload) => {
      return {
        ...s,
        sales: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    pushNewData: (s, payload) => {
      return {
        ...s,
        data: [...s.data, payload]
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setFilters: (s, payload) => {
      return {
        ...s,
        filters: { ...s.filters, ...payload }
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setFiltersItems: (s, payload) => {
      return {
        ...s,
        filtersItems: { ...s.filtersItems, ...payload }
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    replaceRecord: (s, payload) => {
      const _data = s.data
      const index = s.data.findIndex((e) => e.pk === payload.pk)
      const item = s.data.find((e) => e.pk === payload.pk)
      item.status = payload.status
      _data[index] = item

      return {
        ...s,
        data: _data
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setKeyword: (s, payload) => {
      return {
        ...s,
        keyword: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setKeywordItems: (s, payload) => {
      return {
        ...s,
        keyWordItems: payload,
        keywordItems: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setitemPerPage: (s, payload) => {
      return {
        ...s,
        itemPerPage: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setSubItemsPerPage: (s, payload) => {
      return {
        ...s,
        itemPerPageItems: payload
      }
    },
    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setCurrent: (s, payload) => {
      return {
        ...s,
        current: payload
      }
    },

    /**
     *
     * @param {*} s
     * @param {*} payload
     * @returns
     */
    setAction: (s, payload) => {
      return {
        ...s,
        action: payload
      }
    }
  },
  selectors: (slice, createSelector) => ({
    /**
     *
     * @returns
     */
    error() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.showRooms],
        (exhibitions, exhibitionsLoading) => {
          return exhibitionsLoading.error || ""
        }
      )
    },

    /**
     * success selector used in different
     * auth actions
     *
     * @returns {String}
     */
    success() {
      return createSelector(
        [slice, (rootState) => rootState.loading.models.showRooms],
        (exhibitions, exhibitionsLoading) => exhibitionsLoading.success
      )
    },
    currentItem() {
      return createSelector([slice], (exhibitions) => exhibitions.currentItem)
    },
    current() {
      return createSelector([slice], (exhibitions) => exhibitions.current)
    },
    countNew() {
      return createSelector([slice], (exhibitions) => {
        return exhibitions.count_new
      })
    },
    countPending() {
      return createSelector([slice], (exhibitions) => {
        return exhibitions.count_pending
      })
    },
    /**
     *
     * @returns
     */
    paginationData() {
      return createSelector([slice], (exhibitions) => {
        let _data = exhibitions.data
        if (!isEmpty(exhibitions.filters)) {
          const status = exhibitions.filters.status
          _data = !!status
            ? status.value !== "ALL"
              ? _data.filter((e) => e.status.value === status.value)
              : _data
            : _data
        }
        if (!!exhibitions.keyword) {
          const options = {
            keys: ["phone", "title", "titleAr", "whatsapp"]
          }
          const fuse = new Fuse(exhibitions.data, options)
          _data = fuse.search(exhibitions.keyword)
          _data = _data.map((e) => e.item)
        }
        const offset = (exhibitions.currentPage - 1) * exhibitions.itemPerPage
        _data = drop(_data, offset).slice(0, exhibitions.itemPerPage)
        return _data
      })
    },
    paginationDataItems() {
      return createSelector([slice], (exhibitions) => {
        let _data = exhibitions.items

        if (!isEmpty(exhibitions.filtersItems)) {
          const status = exhibitions.filtersItems.status
       _data =
  status && status.value !== "ALL"
    ? _data.filter((e) => {
        const itemStatus = typeof e.status === "object" ? e.status.value : e.status;
        return itemStatus === status.value;
      })
    : _data;
        }
        if (!!exhibitions.keywordItems) {
          const options = {
            keys: ["title","titleAr"]
          }
          const fuse = new Fuse(exhibitions.items, options)
          _data = fuse.search(exhibitions.keywordItems)
          _data = _data.map((e) => e.item)
        }
        const offset =
          (exhibitions.currentPageItems - 1) * exhibitions.subitemPerPage

        _data = drop(_data, offset).slice(0, exhibitions.subitemPerPage)
        return _data
      });
    }
  }),
  effects: (dispatch) => ({
    /**
     *
     */
    async fetchShowRooms(type = "all") {
      appLogger.info("fetchShowRooms", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.FETCH_SHOWROOMS)
      if (shouldSkipProtectedDashboardCalls()) {
        try {
          const remote = await API.get("adminsApi", `/show-rooms/v1?type=${type}`, {})
          this.setData(Array.isArray(remote) ? remote : [])
          this.setAction(globalConst.action.NONE)
          return
        } catch (_adminError) {
          // fallback to local storage
        }
        const localItems = readLocalShowRooms()
        this.setData(localItems)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const result = await API.get(
          "adminsApi",
          `/show-rooms/v1?type=${type}`
        )

        this.setData(result)
        // this.setCount(count)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setData([])
        this.setAction(globalConst.action.NONE)
        return
      }
    },


    async fetchShowRoomsItems(ack) {
      appLogger.info("fetchShowRoomsItems", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.FETCH_SHOWROOMS_ITEMS)
      if (shouldSkipProtectedDashboardCalls()) {
        try {
          const allItems = await API.get("adminsApi", `/show-rooms-items/v1?ack=${ack || 'all'}`, {})
          let normalizedItems = (Array.isArray(allItems) ? allItems : []).map(normalizeShowRoomItem)
          normalizedItems = filterShowRoomItemsByAck(normalizedItems, ack || "all")
          this.setItems(normalizedItems)
          this.setFiltersItems({
            status: { value: "ALL", label: "All" }
          })
          this.setAction(globalConst.action.NONE)
          return
        } catch (_error) {
          this.setItems([])
          this.setAction(globalConst.action.NONE)
          return
        }
      }
      try {
        // Use the ack parameter instead of hardcoding "all"
        const allItems = await API.get(
          "adminsApi",
          `/show-rooms-items/v1?ack=${ack || 'all'}`, // Use the passed parameter
          {}
        );
        
        let normalizedItems = (Array.isArray(allItems) ? allItems : []).map(normalizeShowRoomItem)
        normalizedItems = filterShowRoomItemsByAck(normalizedItems, ack || "all")
        this.setItems(normalizedItems)
        this.setFiltersItems({
          status: { value: "ALL", label: "All" }
        });
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setItems([])
        this.setAction(globalConst.action.NONE)
        return
      }
    },

    async countNewShowRoomsItems() {
      appLogger.info("countNewShowRoomsItems", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.COUNT_NEW_SHOWROOMS_ITEMS)
      try {
        const items = await getAdminsApiDirect(`/show-rooms-items/v1/count?ack=new`)
        this.setCount_new(Number(items) || 0)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        if (handleApiAuthError(error)) return
        appLogger.error("countNewShowRoomsItems", error)
        this.setCount_new(0)
        this.setAction(globalConst.action.NONE)
      }
    },

    async countPendingShowRoomsItems() {
      appLogger.info("countPendingShowRoomsItems", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.COUNT_PENDING_SHOWROOMS_ITEMS)
      try {
        const items = await getAdminsApiDirect(`/show-rooms-items/v1/count?ack=pending`)
        this.setCount_pending(Number(items) || 0)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        if (handleApiAuthError(error)) return
        appLogger.error("countPendingShowRoomsItems", error)
        this.setCount_pending(0)
        this.setAction(globalConst.action.NONE)
      }
    },

    async publishShowRoomsItem(payload) {
      appLogger.info("publishShowRoomsItem", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.PUBLISH_SHOWROOMS_ITEM)
      try {
        const { pk, sk } = payload;
        if (shouldSkipProtectedDashboardCalls()) {
          try {
            await API.put(
              "adminsApi",
              `/show-rooms-items/v1/items/${pk}/publish`,
              { body: { pk, sk } }
            )
          } catch (_error) {
            throw _error
          }
          this.setMessage("Item published successfully");
          this.setAction(globalConst.action.NONE)
          await this.fetchShowRoomsItems("all");
          return
        }
        
        // Call the correct publish endpoint
        const response = await API.put(
          "adminsApi",
          `/show-rooms-items/v1/items/${pk}/publish`,
          {
            body: { pk, sk }
          }
        );
        
        this.setMessage("Item published successfully");
        this.setAction(globalConst.action.NONE)
        
        // Refresh the data to reflect the changes
        await this.fetchShowRoomsItems("all");
        
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        console.error("Error in publishShowRoomsItem:", error);
        throw new Error(error?.message || "Unknown error occurred during publishing");
      }
    },
    async unpublishShowRoomsItem({pk, reason, sk}) {
      if (!pk || !reason) {
        throw new Error("pk and reason are required");
      }
      appLogger.info("unpublishShowRoomsItem", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.UNPUBLISH_SHOWROOMS_ITEM)
      try {
        // Include the sk (timestamp) in the request body
        const requestBody = { pk, reason };
        if (sk) {
          requestBody.sk = sk; // Include sk if provided
        }
        
        const response = await API.put(
          "adminsApi",
          `/show-rooms-items/v1/items/${pk}/unpublish`,
          {
            body: requestBody
          }
        );
        
        // Store the unpublished item info in localStorage
        const unpublishedItemInfo = {
          pk: pk,
          sk: sk, // Include sk in localStorage
          status: "UNPUBLISHED",
          reason: reason,
          updatedAt: new Date().toISOString()
        };
        
        const localUnpublishedItems = JSON.parse(localStorage.getItem('unpublishedShowRoomsItems') || '[]');
        const existingIndex = localUnpublishedItems.findIndex(item => pk === pk);
        
        if (existingIndex >= 0) {
          localUnpublishedItems[existingIndex] = unpublishedItemInfo;
        } else {
          localUnpublishedItems.push(unpublishedItemInfo);
        }
        
        localStorage.setItem('unpublishedShowRoomsItems', JSON.stringify(localUnpublishedItems));
        
        // Refresh the data to include the unpublished item
        await this.fetchShowRoomsItems("all");
        
        // Set filter to show ALL items (both published and unpublished)
        this.setFiltersItems({
          status: { value: "ALL", label: "All" }
        });
        
      } catch (error) {
        console.error("Error in unpublishShowRoomsItem:", error);
        throw new Error(error?.message || "Unknown error occurred during unpublishing");
      }
    },
    
    /**
     * 
     * @param {*} pk 
     */
    async deleteShowRoomItem(pk) {
      appLogger.info("deleteShowRoomItem", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.DELETE_SHOWROOM_ITEM)
      try {
        await API.del(
          "adminsApi",
          `/show-rooms-items/v1/items/${pk}`,
          {}
        )
        
        this.setMessage("Item deleted successfully");
        
        // Remove from local storage if it was there
        const localUnpublishedItems = JSON.parse(localStorage.getItem('unpublishedShowRoomsItems') || '[]');
        const filteredItems = localUnpublishedItems.filter(item => item.pk !== pk);
        localStorage.setItem('unpublishedShowRoomsItems', JSON.stringify(filteredItems));
        
        // Refresh the data to remove the deleted item
        try {
          await this.fetchShowRoomsItems("all");
        } catch (fetchError) {
          console.error("Error refreshing data:", fetchError);
          // Continue anyway - the item was deleted successfully
        }
        
      } catch (error) {
        console.error("Error in deleteShowRoomItem:", error);
        throw new Error(error?.message || "Unknown error occurred during deletion");
      }
    },


    // GET_REALESTATE_ITEM


    async changeItemStatus(payload) {
      appLogger.info("changeItemStatus", `[REDUX][${STORES.SHOWROOMS}]`);
      this.setAction(globalConst.action.CHANGE_SHOWROOMS_ITEM_STATUS);
      if (shouldSkipProtectedDashboardCalls()) {
        try {
          await API.put(
            "adminsApi",
            `/show-rooms-items/v1/items/${payload.pk}/status`,
            { body: { ...payload } }
          )
          const msg = payload.status === "APPROVED" ? "approved" : "rejected";
          this.setMessage(`Item ${msg} successfully`);
          this.setAction(globalConst.action.NONE)
          return
        } catch (_error) {
          this.setAction(globalConst.action.NONE)
          throw new Error(_error)
        }
      }
      try {
        await API.put(
          "adminsApi",
          `/show-rooms-items/v1/items/${payload.pk}/status`,
          {
            body: {...payload}    
          }
        );
        const msg = payload.status === "APPROVED" ? "approved" : "rejected";
        this.setMessage(`Item ${msg} successfully`);
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error);
      }
    },

    async updateShowRoomsItem(payload) {
      appLogger.info("updateShowRoomsItem", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.UPDATE_SHOWROOMS_ITEM)
      try {
     await API.put(
          "adminsApi",
          `/show-rooms-items/v1/items/${payload.pk}`,
          {
            body:{...payload}
          }
        )
        this.setMessage("msg_property_updated_success")
      } catch (error) {
        throw new Error(error)
      }
    },
    // async deleteShowRoomsItem(payload) {
    //   appLogger.info("deleteShowRoomsItem", `[REDUX][${STORES.SHOWROOMS}]`)
    //   this.setAction(globalConst.action.DELETE_SHOWROOMS_ITEM)
    //   try {
    //  await API.del(
    //       "adminsApi",
    //       `/v1/items/${payload.pk}`,
    //       {
    //       }
    //     )
    //     this.setMessage("item deleted successfully")
    //   } catch (error) {
    //     throw new Error(error)
    //   }
    // },
  

    /**
     * 
     * @param {*} pk 
     */
    async getShowRoomsItem(pk) {
      appLogger.info("getShowRoomsItem", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.GET_SHOWROOMS_ITEM)
      try {
        const raw = await API.get(
          "adminsApi",
          `/show-rooms-items/v1/items/${pk}`,
          {
          }
        )
        const normalized = normalizeShowRoomItem(Array.isArray(raw) ? raw[0] || {} : raw || {})
        this.setCurrentItem(normalized)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        throw new Error(error)
      }
    },
    /**
     * 
     */
    async getShowRoom(pk) {
      appLogger.info("getShowRoom", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.GET_SHOWROOM)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend() && pk) {
          try {
            const item = await v2Request(`/show-rooms/v1/${pk}`)
            this.setCurrent(item || {})
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage
          }
        }
        const localItem = findLocalShowRoom(pk) || {}
        this.setCurrent(localItem)
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
        const result = await API.get(
          "adminsApi",
          `/show-rooms/v1/${pk}`
        )

        this.setCurrent(result)
        // this.setCount(count)
        this.setAction(globalConst.action.NONE)
      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },
    /**
     * 
     * @param {*} payload 
     */
    async saveShowRoom(payload) {
      appLogger.info("saveShowRoom", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.SAVE_SHOWROOM)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend()) {
          try {
            await v2Request("/show-rooms/v1", {
              method: "POST",
              body: payload
            })
            const items = await v2Request("/show-rooms/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_showroom_saved_succes")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage
          }
        }
        const now = new Date().toISOString()
        const localRecord = {
          ...payload,
          pk: `local-showroom-${Date.now()}`,
          status: "CREATED",
          createdAt: now,
          lmb: payload?.email || "local-admin"
        }
        const existing = readLocalShowRooms()
        const next = [localRecord, ...existing]
        writeLocalShowRooms(next)
        this.setData(next)
        this.setMessage("msg_showroom_saved_succes")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {

         await API.post(
          "adminsApi",
          `/show-rooms/v1`,
          {
            body:payload
          }
        )

        this.setMessage("msg_showroom_saved_succes")
        this.setAction(globalConst.action.NONE)

      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },
    /**
     * 
     * @param {*} payload 
     */
    async updateShowRoom(payload) {
      appLogger.info("updateShowRoom", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.UPDATE_SHOWROOM)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend() && payload?.pk) {
          try {
            await v2Request(`/show-rooms/v1/${payload.pk}`, {
              method: "PUT",
              body: omit(payload, ["pk"])
            })
            const items = await v2Request("/show-rooms/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setCurrent((Array.isArray(items) ? items : []).find((item) => item.pk === payload.pk) || {})
            this.setMessage("msg_showroom_update_succes")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage
          }
        }
        const now = new Date().toISOString()
        const next = upsertLocalShowRoom(payload.pk, {
          ...omit(payload, ["pk"]),
          updatedAt: now
        })
        this.setData(next)
        this.setCurrent(findLocalShowRoom(payload.pk) || {})
        this.setMessage("msg_showroom_update_succes")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {

         await API.put(
          "adminsApi",
          `/show-rooms/v1/${payload.pk}`,
          {
            body:omit(payload, ["pk"])
          }
        )

        this.setMessage("msg_showroom_update_succes")
        this.setAction(globalConst.action.NONE)

      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },
    /**
     * 
     * @param {*} payload 
     */
    async activateShowRoom(payload) {
      appLogger.info("activateShowRoom", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.ACTIVATE_SHOWROOM)
      if (shouldSkipProtectedDashboardCalls() || isLocalShowRoomPk(payload?.pk)) {
        if (hasV2Backend() && payload?.pk) {
          try {
            await v2Request(`/show-rooms/v1/${payload.pk}/activate`, {
              method: "PUT",
              body: omit(payload, ["pk"])
            })
            const items = await v2Request("/show-rooms/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_showroom_activate_succes")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage
          }
        }
        const next = upsertLocalShowRoom(payload.pk, {
          status: "ACTIVE",
          updatedAt: new Date().toISOString()
        })
        this.setData(next)
        this.setMessage("msg_showroom_activate_succes")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {
    

         await API.put(
          "adminsApi",
          `/show-rooms/v1/${payload.pk}/activate`,
          {
            body: omit(payload, ["pk"])
          }
        )

        this.setMessage("msg_showroom_activate_succes")
        this.setAction(globalConst.action.NONE)

      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },
    /**
     * 
     * @param {*} payload 
     */
    async resetShowRoom(payload) {
      appLogger.info("resetShowRoom", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.RESET_SHOWROOM)
      if (shouldSkipProtectedDashboardCalls() || isLocalShowRoomPk(payload?.pk)) {
        if (hasV2Backend() && payload?.pk) {
          try {
            await v2Request(`/show-rooms/v1/${payload.pk}/reset`, {
              method: "POST",
              body: omit(payload, ["pk"])
            })
            this.setMessage("msg_showroom_reset_password_succes")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local success
          }
        }
        this.setMessage("msg_showroom_reset_password_succes")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {

         await API.post(
          "adminsApi",
          `/show-rooms/v1/${payload.pk}/reset`,
          {
            body:omit(payload, ["pk"])
          }
        )

        this.setMessage("msg_showroom_reset_password_succes")
        this.setAction(globalConst.action.NONE)

      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },
    /**
     * 
     * @param {*} payload 
     */
    async enableShowRoom(payload) {
      appLogger.info("enableShowRoom", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.ENABLE_SHOWROOM)
      if (shouldSkipProtectedDashboardCalls() || isLocalShowRoomPk(payload?.pk)) {
        if (hasV2Backend() && payload?.pk) {
          try {
            await v2Request(`/show-rooms/v1/${payload.pk}/enable`, {
              method: "PUT",
              body: {}
            })
            const items = await v2Request("/show-rooms/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_showroom_enable_succes")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage
          }
        }
        const next = upsertLocalShowRoom(payload.pk, {
          status: "ACTIVE",
          updatedAt: new Date().toISOString()
        })
        this.setData(next)
        this.setMessage("msg_showroom_enable_succes")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {

         await API.put(
          "adminsApi",
          `/show-rooms/v1/${payload.pk}/enable`,
          {
          }
        )

        this.setMessage("msg_showroom_enable_succes")
        this.setAction(globalConst.action.NONE)

      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },
    /**
     * 
     * @param {*} payload 
     */
    async disableShowRoom(payload) {
      appLogger.info("disableShowRoom", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.DISABLED_SHOWROOM)
      if (shouldSkipProtectedDashboardCalls() || isLocalShowRoomPk(payload?.pk)) {
        if (hasV2Backend() && payload?.pk) {
          try {
            await v2Request(`/show-rooms/v1/${payload.pk}/disable`, {
              method: "PUT",
              body: {}
            })
            const items = await v2Request("/show-rooms/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setMessage("msg_showroom_disable_succes")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage
          }
        }
        const next = upsertLocalShowRoom(payload.pk, {
          status: "DISABLED",
          updatedAt: new Date().toISOString()
        })
        this.setData(next)
        this.setMessage("msg_showroom_disable_succes")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {

         await API.del(
          "adminsApi",
          `/show-rooms/v1/${payload.pk}/disable`
        )

        this.setMessage("msg_showroom_disable_succes")
        this.setAction(globalConst.action.NONE)

      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },

    /**
     *
     * @param {*} pk
     */

    async deleteShowRoom(pk) {
      appLogger.info("deleteShowRoom", `[REDUX][${STORES.SHOWROOMS}]`)
      this.setAction(globalConst.action.DELETE_SHOWROOM)
      if (shouldSkipProtectedDashboardCalls()) {
        if (hasV2Backend() && pk) {
          try {
            await v2Request(`/show-rooms/v1/${pk}`, { method: "DELETE" })
            const items = await v2Request("/show-rooms/v1")
            this.setData(Array.isArray(items) ? items : [])
            this.setCurrent({})
            this.setMessage("msg_showroom_delete_success")
            this.setAction(globalConst.action.NONE)
            return
          } catch (_error) {
            // fallback to local storage
          }
        }
        const next = removeLocalShowRoom(pk)
        this.setData(next)
        this.setCurrent({})
        this.setMessage("msg_showroom_delete_success")
        this.setAction(globalConst.action.NONE)
        return
      }
      try {

         await API.del(
          "adminsApi",
          `/show-rooms/v1/${pk}`
        )

        this.setMessage("msg_showroom_delete_success")
        this.setAction(globalConst.action.NONE)

      } catch (error) {
        this.setAction(globalConst.action.NONE)
        throw new Error(error)
      }
    },

    async deleteAgenceItem({ pk, createdAt }) {
      appLogger.info(`deleteAgenceItem ${pk}`, `[REDUX][${STORES.EXHIBITIONS}]`)
      this.setAction(globalConst.action.DELETE_AGENCES_ITEM)
      try {
        await API.post("exhibitionsApiGateway", `/exibitions/${pk}/rm`, {
          body: { createdAt }
        })
        this.setMessage("msg_agency_deleted_sucess")
      } catch (error) {
        throw new Error(error)
      }
    },


    /**
     *
     */
    onAcionReset() {
      this.setAction(globalConst.action.NONE)
    },
    /**
     *
     * @param {*} param0
     */
    resetCurrentRecord(payload = {}) {
      this.setCurrent(payload)
    },
    resetCurrentItem(payload = {}) {
      this.setCurrentItem(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onItemPerPageChange(payload) {
      this.setitemPerPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onSubItemPerPageChange(payload) {
      this.setSubItemsPerPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onFiltersChange(payload) {
      this.setFilters(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onCurrentPageChange(payload) {
      this.setCurrentPage(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onItemsCurrentPageChange(payload) {
      this.setCurrentPageItems(payload)
    },
    onCurrentPageSubChange(payload) {
      this.setCurrentPageItems(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onKeywordChange(payload) {
      this.setKeyword(payload)
    },
    /**
     *
     * @param {*} payload
     */
    onItemKeywordChanges(payload) {
      this.setKeywordItems(payload)
    },
    onKeywordItemsChange(payload) {
      this.setKeywordItems(payload)
    }
  })
}
